/** The dashboard's data layer: three queries, not one, because they have different audiences and failure modes (`/me` personal, `/company` shared and cacheable, `/restricted` 403s for most by design).
 *  The restricted query is gated on the client too only to avoid a request guaranteed to 403; the security boundary is the server's (api/routers/dashboard.py). */
import { useEffect, useRef } from 'react'

import { useIsMutating, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import { can, type User } from './types'

export interface Bucket {
  label: string
  value: number
}

interface SeriesPoint {
  label: string
  a: number
  b: number
}

export interface Personal {
  employee_id: string
  balance: {
    al_days: number
    psl_days: number
    cl_days: number
    /** Never stored: a rolling 28-day computed entitlement. */
    rl_days: number
    spl_days: number
    taken_sl_days: number
  }
  /** The current payroll period so far (not the calendar month); the seven counts sum to `period_days`. */
  timesheet: {
    period_start: string
    period_days: number
    approved: number
    pending: number
    draft: number
    rejected: number
    missing: number
    weekend: number
    holiday: number
  }
  by_activity: Bucket[]
  by_product: Bucket[]
  hours_trend: SeriesPoint[]
  attention: {
    rejected_days: number
    open_drafts: number
    pending_days: number
    pending_leave: number
  }
  awaiting: { own: number; team: number; all: number; mine: number }
  next_day_off: { day: string; kind: string; label: string; days_away: number } | null
  desk_week: { day: string; desk: string; morning: boolean; afternoon: boolean }[]
  /** `null` means "not yours to see"; 0 means "none". */
  open_items: {
    draft_sales: number | null
    pending_requests: number | null
    unapproved_trips: number | null
  }
  /** `time_day.status == 'Pending'` for supervisees this viewer owns. */
  supervisee_hours_awaiting: number
  /** `null` means nothing is currently assigned, not a zero-ish placeholder. */
  my_laptop: { asset_tag: string; model: string } | null
}

export interface Company {
  hours_by_product: Bucket[]
  /** Same TimeLog rows as `hours_by_product`, grouped by `StaffInfo.work_type`. */
  hours_by_work_type: Bucket[]
  backlog: { buckets: Bucket[]; total: number; oldest_days: number }
  away: { day: string; count: number; holiday: string }[]
  /** Who is away on the server's business day; `null` when withheld (no `staff`), never an empty "nobody". */
  away_today: { day: string; count: number; names: string[] } | null
  headcount: {
    active: number
    inactive: number
    by_team: Bucket[]
    by_office: Bucket[]
    by_work_type: Bucket[]
    by_gender: Bucket[]
    /** Absent from a server older than the field; the Full-time toggle is then not offered. */
    by_gender_full_time?: Bucket[]
  }
  org: { by_product: Bucket[]; role_mix: Bucket[]; spans: Bucket[]; uncovered: string[] }
  desks: {
    total: number
    booked_today: number
    free_today: number
    by_weekday: Bucket[]
    top_desks: Bucket[]
  }
  work_mode_today: { mode: string; people: string[] }[]
}

interface PersonDate {
  name: string
  day: string
  detail: string
}

export interface Restricted {
  /** Which gates opened. The page renders from THIS, not from which keys are non-null: a withheld block is absent, never zeroed. */
  granted: string[]
  payroll: {
    gross_net: SeriesPoint[]
    contributions: SeriesPoint[]
    headcount: number
    latest_month: string
    latest_gross: number
    latest_net: number
    /** Per month, aligned with `gross_net`: the issued runs and how many never had net/social/tax recorded (left out of every sum). Absent from an older server. */
    runs?: number[]
    not_recorded?: number[]
    // Grouped by the literal `EmployeeRate.type_of` value; see _rate_bands in services/dashboard.py.
    rate_bands: { basis: string; bands: Bucket[] }[]
    draft_runs: number
  } | null
  liability: {
    total_days: number
    people: number
    by_team: Bucket[]
    top_balances: Bucket[]
  } | null
  people: {
    birthdays: PersonDate[]
    anniversaries: PersonDate[]
    contracts_ending: PersonDate[]
    tenure: Bucket[]
  } | null
  approvals: { by_approver: Bucket[]; oldest: Bucket[]; unassigned: number } | null
  /** Same gate as `payroll` (`salary.view`). */
  finance: {
    sales_this_month: number
    sales_issued_count: number
    /** Not bounded to this month: a receivable does not stop being owed when the month turns over. */
    sales_outstanding: number
    activity_balance: number
    /** Company-wide, not "mine". */
    open_requests: number
    open_trips: number
    /** `null` only when no `SalaryPeriod` row exists on or after today. */
    salary_cutoff_date: string | null
    days_until_salary_cutoff: number | null
  } | null
}

/** The four abilities `/api/dashboard/restricted` gates on, mirroring RESTRICTED_ABILITIES in the router. */
const RESTRICTED_ABILITIES = [
  'salary.view',
  'leave.admin',
  'hr.view',
  'time.admin',
] as const

function holdsAnyRestricted(user: User | undefined): boolean {
  return RESTRICTED_ABILITIES.some((ability) => can(user, ability))
}

const dashboardKeys = {
  me: ['dashboard', 'me'] as const,
  company: ['dashboard', 'company'] as const,
  restricted: ['dashboard', 'restricted'] as const,
}

/** One definition of the `/me` query, shared by the dashboard page and the Time nav badge so they cannot drift into two caches of the same route. */
const personalQuery = {
  queryKey: dashboardKeys.me,
  queryFn: async () => (await api.get<Personal>('/api/dashboard/me')).data,
  staleTime: 60_000,
}

export function usePersonalDashboard() {
  return useQuery(personalQuery)
}

const finite = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? n : 0)

/** Leave requests waiting on this viewer's decision (the Leave nav badge): `awaiting.mine` is leave only. ⛔ Not `/api/notifications/unread-count`. */
export function leaveApprovalCount(data: Personal | undefined): number {
  return data ? finite(data.awaiting?.mine) : 0
}

/** Supervisee timesheet days waiting on this viewer (the Time nav badge): Pending `time_day` for supervisees. */
export function timeApprovalCount(data: Personal | undefined): number {
  return data ? finite(data.supervisee_hours_awaiting) : 0
}

/** What the badge shows, or `null` to render nothing (not a "0" chip); capped so a large backlog does not blow out the rail icon. */
export function navBadgeText(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null
  return count > 99 ? '99+' : String(count)
}

/** The badge's own observer over the same `/me` cache the dashboard reads, which is why it reuses the route instead of adding a count endpoint.
 *  Refresh: `staleTime` 60s; `refetchOnWindowFocus` ON HERE ONLY; and `/me` is invalidated whenever the in-flight mutation count settles to zero.
 *  ⚠ `useIsMutating` settles on a failed mutation too and this is mounted on every page, so it fires on any mutation finishing anywhere: one small refetch each time, the price of needing no cooperation from other pages. */
export function useApprovalsAwaiting(): { time: number; leave: number } {
  const client = useQueryClient()
  const mutating = useIsMutating()
  const wasMutating = useRef(mutating)
  useEffect(() => {
    if (wasMutating.current > 0 && mutating === 0) {
      void client.invalidateQueries({ queryKey: dashboardKeys.me })
    }
    wasMutating.current = mutating
  }, [mutating, client])

  const { data } = useQuery({ ...personalQuery, refetchOnWindowFocus: true })
  return { time: timeApprovalCount(data), leave: leaveApprovalCount(data) }
}

export function useCompanyDashboard() {
  return useQuery({
    queryKey: dashboardKeys.company,
    queryFn: async () => (await api.get<Company>('/api/dashboard/company')).data,
    staleTime: 60_000,
  })
}

export function useRestrictedDashboard(user: User | undefined) {
  return useQuery({
    queryKey: dashboardKeys.restricted,
    queryFn: async () => (await api.get<Restricted>('/api/dashboard/restricted')).data,
    // Noise suppression, not the gate; see the module docstring.
    enabled: holdsAnyRestricted(user),
    staleTime: 60_000,
    retry: false,
  })
}
